import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { compact, includes, keyBy, map, reject, without } from 'lodash-es';
import { Box, Heading, Paragraph, Toast, useToast } from '@zillow/constellation-10';
import type { ApplicationConfigurationCustomQuestion } from '@zg-rentals/ts-capps-client';
import {
  useCappsRenterDocumentsWebApiHandler_listActiveDocuments,
  useCappsRenterDocumentsWebApiHandler_requestUserToken,
} from '@zg-rentals/ts-capps-client-react-query';
import type { ZDocsUpload } from '@zillow/zdocs-web';
import { ZDocsUploader } from '@zillow/zdocs-web';
import type { Document } from '../../../../../models/rental-application';
import { useIncomeDocsRedaction } from '../../../../../hooks/useIncomeDocsRedaction';
import {
  useAddRequiredDocuments,
  useAttachRequiredDocuments,
  useDetachRequiredDocument,
} from '../../../../../hooks/useRequiredDocumentMutations';
import { toDocument } from '../../../../../hooks/useRetrieveActiveDocuments';
import { DOCUMENT_POOL_TAG } from '../../../../../constants/applicationsConstants';
import { getPickableDocuments } from '../../../../../utils/incomeDocuments';
import { CUSTOM_QUESTION_DOCUMENTS_LIMIT } from '../../../../../utils/customQuestions';
import { DocumentCardMenu } from '../../Documents/DocumentCardMenu';
import { DocumentCardSkeleton } from '../../Documents/DocumentCardSkeleton';
import { IdentifySavedDocuments } from '../../Documents/IdentifySavedDocuments';
import { PetDocumentCard } from './PetDocumentCard';

const ACCEPTED_FILES = ['.pdf', '.jpeg', '.jpg', '.png'];

type PendingUpload = {
  messageToken: string;
  fileName: string;
  documentId?: string;
};

type ListedDocument = {
  key: string;
  document: Document;
  isLoading: boolean;
};

type Props = {
  applicationId: string;
  question: ApplicationConfigurationCustomQuestion;
  heading: string;
  savedLabel: string;
  documentLabel: string;
  description?: ReactNode;
  documents: Array<Document>;
};

export function CustomQuestionDocumentsField({
  applicationId,
  question,
  heading,
  savedLabel,
  documentLabel,
  description,
  documents,
}: Props) {
  const { enqueueToast } = useToast();
  const [pendingUploads, setPendingUploads] = useState<Array<PendingUpload>>([]);
  const [hiddenIds, setHiddenIds] = useState<Array<string>>([]);
  const [uploaderKey, setUploaderKey] = useState(0);
  const { documents: details, isError: isDetailsError } = useIncomeDocsRedaction(documents);
  const { mutateAsync: requestUserToken } = useCappsRenterDocumentsWebApiHandler_requestUserToken({
    meta: { context: { api: 'cappsRenterDocuments', details: 'requestUserToken' } },
  });
  const { mutateAsync: addDocuments } = useAddRequiredDocuments();
  const { mutateAsync: detachDocument } = useDetachRequiredDocument();
  const { mutateAsync: attachDocuments } = useAttachRequiredDocuments();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { data: savedDocuments } = useCappsRenterDocumentsWebApiHandler_listActiveDocuments({
    meta: {
      tags: [DOCUMENT_POOL_TAG],
      context: { api: 'cappsRenterDocuments', details: 'listActiveDocuments' },
    },
  });

  const detailsById = keyBy(details, 'id');
  const pendingById = keyBy(pendingUploads, 'documentId');
  const savedIds = compact(map(documents, 'id'));
  const visibleDocuments = documents
    .filter((document) => !document.id || !hiddenIds.includes(document.id))
    .map((document) => detailsById[document.id ?? ''] ?? document);
  const listedDocuments: Array<ListedDocument> = [
    ...visibleDocuments.map((document, index) => {
      const pending = document.id ? pendingById[document.id] : undefined;
      const isLoading = !!document.id && !detailsById[document.id] && !isDetailsError;
      return {
        key: document.id ?? `saved-${index}`,
        document: isLoading && pending ? { ...document, fileName: pending.fileName } : document,
        isLoading: isLoading && !pending,
      };
    }),
    ...pendingUploads
      .filter((upload) => !upload.documentId || !includes(savedIds, upload.documentId))
      .map((upload) => ({ key: upload.messageToken, document: { fileName: upload.fileName }, isLoading: false })),
  ];

  useEffect(() => {
    const loadedIds = compact(map(details, 'id'));
    setPendingUploads((current) => reject(current, (upload) => includes(loadedIds, upload.documentId)));
  }, [details]);
  const remaining = CUSTOM_QUESTION_DOCUMENTS_LIMIT - listedDocuments.length;
  const attachedIds = compact(map(visibleDocuments, 'id'));
  const candidates = getPickableDocuments((savedDocuments?.documentInfoList ?? []).map(toDocument), {
    now: Date.now(),
    isExcluded: (documentId) => attachedIds.includes(documentId),
  });

  const hide = (documentId: string) => setHiddenIds((current) => [...current, documentId]);
  const unhide = (documentId: string) => setHiddenIds((current) => without(current, documentId));

  async function generateUploadToken() {
    const result = await requestUserToken();
    return result.userToken ?? '';
  }

  async function onUploadComplete(upload: ZDocsUpload) {
    const { messageToken } = upload;
    if (!messageToken) {
      enqueueToast(<Toast tone="critical">We couldn’t upload this document. Try again.</Toast>);
      return;
    }
    const pending = { messageToken, fileName: upload.file.name.replace(/\.[^.]+$/, '') };
    setPendingUploads((current) => [...current, pending]);
    try {
      const [uploaded] = await addDocuments({
        postData: { applicationId, customQuestion: question, documents: [{ messageToken }] },
      });
      setPendingUploads((current) =>
        uploaded
          ? current.map((upload) =>
              upload.messageToken === messageToken ? { ...upload, documentId: uploaded.documentId } : upload,
            )
          : reject(current, { messageToken }),
      );
    } catch {
      enqueueToast(<Toast tone="critical">We couldn’t save this document. Try again.</Toast>);
      setPendingUploads((current) => reject(current, { messageToken }));
    }
  }

  async function onRemove(documentId: string) {
    hide(documentId);
    try {
      await detachDocument({ postData: { applicationId, customQuestion: question, documentId } });
    } catch {
      unhide(documentId);
      enqueueToast(<Toast tone="critical">We couldn’t remove this document. Try again.</Toast>);
    }
  }

  async function onAddSaved(documentIds: Array<string>) {
    setHiddenIds((current) => without(current, ...documentIds));
    try {
      await attachDocuments({ postData: { applicationId, customQuestion: question, documentIds } });
    } catch {
      enqueueToast(<Toast tone="critical">We couldn’t add these documents. Try again.</Toast>);
    }
  }

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tightest' }}>
        <Heading ref={headingRef} tabIndex={-1} level={4} css={{ textStyle: 'body-bold' }}>
          {heading}
        </Heading>
        {description}
      </Box>
      {listedDocuments.length > 0 && (
        <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter' }}>
          {listedDocuments.map(({ key, document, isLoading }) => (
            <Box key={key} css={{ gridColumnEnd: { base: 'span 12', md: 'span 6', lg: 'span 4' } }}>
              {isLoading ? (
                <DocumentCardSkeleton />
              ) : (
                <PetDocumentCard
                  document={document}
                  documentLabel={documentLabel}
                  onDeleted={hide}
                  menu={({ onDelete, name }) => (
                    <DocumentCardMenu
                      name={name}
                      removeLabel="Remove from this application"
                      onRemove={document.id ? () => void onRemove(document.id as string) : undefined}
                      onDelete={onDelete}
                      disabled={!document.id}
                    />
                  )}
                />
              )}
            </Box>
          ))}
        </Box>
      )}
      {remaining > 0 && (
        <ZDocsUploader
          key={uploaderKey}
          generateUploadToken={generateUploadToken}
          onUploadComplete={onUploadComplete}
          onUploadActive={(isUploadActive) => {
            if (!isUploadActive) {
              setUploaderKey((current) => 1 - current);
            }
          }}
          onUploadFailed={(failed) => enqueueToast(<Toast tone="critical">{failed.errorMessage}</Toast>)}
          placeholderText={`Add up to ${CUSTOM_QUESTION_DOCUMENTS_LIMIT} files. Password-protected and encrypted files aren’t supported.`}
          accept={ACCEPTED_FILES}
          autoUpload
          maxFiles={remaining}
        />
      )}
      {remaining > 0 && candidates.length > 0 && (
        <IdentifySavedDocuments
          typeLabel={savedLabel}
          candidates={candidates}
          maxSelectable={remaining}
          onAdd={(documentIds) => void onAddSaved(documentIds)}
          focusHeading={() => headingRef.current?.focus()}
        />
      )}
      {remaining <= 0 && (
        <Paragraph textStyle="body-sm" color="text.subtle">
          You added the maximum of {CUSTOM_QUESTION_DOCUMENTS_LIMIT} files.
        </Paragraph>
      )}
    </Box>
  );
}
